import * as React from "react";
import { useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useAppLocale } from "@/lib/i18n";
import { sortByName } from "@/lib/sort";

type NamedReferenceItem = {
  _id: string;
  name?: string | null;
};

function useSortedNamedReferenceData<T extends NamedReferenceItem>(queryResult: T[] | undefined) {
  const { locale } = useAppLocale();

  return React.useMemo(
    () => sortByName(queryResult, locale),
    [locale, queryResult],
  );
}

export function useOrigins() {
  const { authUserId } = useAuthIdentity();
  const origins = useQuery(
    api.admin.getAllOrigins,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(origins);
}

export function useRegions() {
  const { authUserId } = useAuthIdentity();
  const regions = useQuery(
    api.admin.getAllRegions,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(regions);
}

export function useProducers() {
  const { authUserId } = useAuthIdentity();
  const producers = useQuery(
    api.admin.getAllProducers,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(producers);
}

export function useFlavors() {
  const { authUserId } = useAuthIdentity();
  const flavors = useQuery(
    api.admin.getAllFlavors,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(flavors);
}

export function useAromas() {
  const { authUserId } = useAuthIdentity();
  const aromas = useQuery(
    api.admin.getAllAromas,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(aromas);
}

export function useWarehouses() {
  const { authUserId } = useAuthIdentity();
  const warehouses = useQuery(
    api.admin.getAllWarehouses,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(warehouses);
}

export function usePallets() {
  const { authUserId } = useAuthIdentity();
  const pallets = useQuery(
    api.admin.getAllPallets,
    authUserId ? { authUserId } : "skip",
  ) as NamedReferenceItem[] | undefined;

  return useSortedNamedReferenceData(pallets);
}
